import { useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { useNavigation } from "utils/navigate";
import { fetchUserDetails } from "Layout/slice/thunk";
import { Button } from "components/Button";
import { Loader } from "components/Loader";
import { TaxW9Agreement } from "shared/SurmountAccount/TaxW9Agreement/index";
import { SignAgreement } from "shared/SurmountAccount/SignAgreement/index";
import StatusScreen from "shared/Modal/CreateSurmountAccountModal/StatusScreen";
import {
  SignupFlowFooter,
  SignupFlowContent,
  SignupFlowWrapperContainer,
} from "../../../Components/SignupFlowWrapper.styles";
import { toast } from "Layout/slice";
import { useMutation } from "hooks/useMutation";
import {
  updateSignAgreement,
  createAlpacaUser,
} from "features/SurmountAccount/service";
import { PrismCoinIcon, AllSetICon } from "assets/icons";
import { FooterRightWrapper, TaxCardWrapper } from "./TaxW9Agreement.styles";
import { parseErrorMessage } from "utils/index";
import PropTypes from "prop-types";

const STEPS = {
  AGREEMENT: "AGREEMENT",
  PROCESSING: "PROCESSING",
  ALL_SET: "ALL_SET",
};

const SurmountAccountTaxW9Agreement = ({ showStatusScreens = true }) => {
  const { navigateToSignupIntroQuestions, navigateToSignupFundYourAccount } =
    useNavigation();
  const dispatch = useDispatch();

  const [agreementValues, setAgreementValues] = useState({
    agree: false,
    account_agreement: false,
    customer_agreement: false,
  });
  const [activeStep, setActiveStep] = useState(STEPS.AGREEMENT);
  const submissionCompletedRef = useRef(false);

  const areAllAgreementsAccepted =
    agreementValues.agree &&
    agreementValues.account_agreement &&
    agreementValues.customer_agreement;

  const { mutate: submitAgreement, isLoading: isSubmitting } = useMutation({
    mutationFn: updateSignAgreement,
    onSuccess: async () => {
      try {
        submissionCompletedRef.current = true;
        // Create Alpaca user after agreement is signed
        await createAlpacaUser();
        // Refresh user details to get updated account status
        dispatch(fetchUserDetails());
        if (showStatusScreens) {
          setActiveStep(STEPS.ALL_SET);
        } else {
          navigateToSignupFundYourAccount();
        }
      } catch (error) {
        // Show error message from backend
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
        // Refresh user details and redirect to dashboard even if account already exists
        dispatch(fetchUserDetails());
        if (showStatusScreens) {
          setActiveStep(STEPS.AGREEMENT);
          submissionCompletedRef.current = false;
        }
      }
    },
    onError: (error) => {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
      if (showStatusScreens) {
        setActiveStep(STEPS.AGREEMENT);
        submissionCompletedRef.current = false;
      }
    },
  });

  const handleNext = () => {
    if (!areAllAgreementsAccepted) {
      dispatch(
        toast({
          type: "error",
          body: "Please accept all agreements to proceed.",
        })
      );
      return;
    }

    submissionCompletedRef.current = false;
    if (showStatusScreens) {
      setActiveStep(STEPS.PROCESSING);
    }

    const payload = {
      margin_agreement: agreementValues.agree,
      account_agreement: agreementValues.account_agreement,
      customer_agreement: agreementValues.customer_agreement,
    };

    submitAgreement(payload);
  };

  const handleProcessingContinue = () => {
    if (!submissionCompletedRef.current) return;
    setActiveStep(STEPS.ALL_SET);
  };

  const handleAllSetContinue = () => {
    navigateToSignupFundYourAccount();
  };

  if (showStatusScreens && activeStep === STEPS.PROCESSING) {
    return (
      <StatusScreen
        variant="processing"
        icon={PrismCoinIcon}
        title="Processing Application..."
        description="We're processing your application. This might take 1-2 days."
        showButton={false}
        onContinue={handleProcessingContinue}
      />
    );
  }

  if (showStatusScreens && activeStep === STEPS.ALL_SET) {
    return (
      <StatusScreen
        variant="allSet"
        icon={AllSetICon}
        title="You're all set"
        description="We're processing your application. This might take a minute."
        buttonLabel="Continue"
        onContinue={handleAllSetContinue}
      />
    );
  }

  return (
    <>
      <SignupFlowWrapperContainer>
        <SignupFlowContent>
          <div className="header">
            <h3>Take a moment to review your agreement</h3>
            <p>Please take a moment to review your agreements carefully</p>
          </div>
          <TaxCardWrapper>
            <TaxW9Agreement />

            <SignAgreement
              onAgreementChange={setAgreementValues}
              agreementValues={agreementValues}
            />
          </TaxCardWrapper>
        </SignupFlowContent>

        <SignupFlowFooter>
          <FooterRightWrapper>
            <Button
              variant="secondary"
              onClick={navigateToSignupIntroQuestions}
            >
              Go back
            </Button>
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={isSubmitting || !areAllAgreementsAccepted}
            >
              {isSubmitting ? (
                <Loader className="on-btn" />
              ) : (
                "Submit application"
              )}
            </Button>
          </FooterRightWrapper>
        </SignupFlowFooter>
      </SignupFlowWrapperContainer>
    </>
  );
};

export default SurmountAccountTaxW9Agreement;

SurmountAccountTaxW9Agreement.propTypes = {
  showStatusScreens: PropTypes.bool,
};
